import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { usePresence } from '../../../hooks/usePresence';
import { supabase } from '../../../utils/supabase';

const ProfileHero = ({ profile, onEditProfile, onShareProfile, completionPercentage = 0, isPublicView = false, aiAssessments: passedAiAssessments }) => {
  // Handle loading state when profile is null or undefined
  const isLoading = !profile;
  const [showCompletionDetails, setShowCompletionDetails] = useState(false);
  const [aiAssessments, setAiAssessments] = useState(new Map());
  const navigate = useNavigate();
  const { getUserPresence } = usePresence();
  
  // Get real-time presence data for this user
  const presenceData = profile?.id ? getUserPresence(profile.id) : null;

  // Fetch AI skill assessments from database
  // Strategy: For public profiles, use passed assessments (to avoid duplicate DB calls)
  // For own profile, fetch from database directly for real-time updates
  React.useEffect(() => {
    // If passed assessments provided (public view), use those directly
    if (passedAiAssessments) {
      setAiAssessments(passedAiAssessments);
      return;
    }

    // Otherwise fetch for own profile
    const fetchAiAssessments = async () => {
      if (!profile?.id || isPublicView) return;

      try {
        const { data, error } = await supabase
          .from('ai_skill_assessment_sessions')
          .select('skill_name, final_score, proficiency_level')
          .eq('user_id', profile.id)
          .eq('passed', true)
          .order('updated_at', { ascending: false });

        if (error) throw error;

        // Create map of AI assessments by skill name (latest passing assessment for each skill)
        // Query already sorted by updated_at DESC, so first encounter is most recent
        const assessmentMap = new Map();
        if (data && data.length > 0) {
          data.forEach(assessment => {
            // Store only the first/latest assessment for each skill
            if (!assessmentMap.has(assessment.skill_name)) {
              assessmentMap.set(assessment.skill_name, assessment);
            }
          });
        }
        setAiAssessments(assessmentMap);
      } catch (error) {
        console.error('Error fetching AI assessments in ProfileHero:', error);
      }
    };

    fetchAiAssessments();
  }, [profile?.id, isPublicView, passedAiAssessments]);

  // Currency symbol helper
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'AUD': 'A$',
      'CAD': 'C$',
      'CHF': 'CHF',
      'CNY': '¥',
      'INR': '₹',
      'PHP': '₱'
    };
    return symbols[currency] || currency || '$';
  };

  const handleShareProfile = () => {
    if (!profile) return;
    
    // Construct the public profile URL
    const publicProfileUrl = profile.public_username 
      ? `${window.location.origin}/va/${profile.public_username}`
      : window.location.href;
    
    if (navigator.share) {
      navigator.share({
        title: `${profile?.full_name || 'Virtual Assistant'} - Virtual Assistant`,
        text: profile.bio || profile.headline || 'Professional Virtual Assistant',
        url: publicProfileUrl,
      });
    } else {
      navigator.clipboard.writeText(publicProfileUrl);
      onShareProfile?.();
    }
  };

  const handleEditProfile = () => {
    // Navigate to profile settings page
    navigate('/va-profile-settings');
  };

  const getOnlineStatusIndicator = () => {
    // Use real presence data if available, fallback to availability_status
    const status = presenceData?.status || profile?.availability_status;
    
    if (!status) return 'bg-gray-500'; // Offline/unknown
    if (typeof status !== 'string') return 'bg-gray-500'; // Not a string
    
    // Handle presence status
    if (status === 'online') return 'bg-green-500';
    if (status === 'away' || status === 'idle') return 'bg-yellow-500';
    if (status === 'offline') return 'bg-gray-500';
    
    // Handle availability status (fallback)
    switch(status.toLowerCase()) {
      case 'available':
        return 'bg-green-500';
      case 'partially_available':
      case 'busy':
        return 'bg-yellow-500';
      case 'unavailable':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = () => {
    const status = presenceData?.status || profile?.availability_status;
    
    if (!status) return 'X';
    
    // Handle presence status
    if (status === 'online') return 'Check';
    if (status === 'away' || status === 'idle') return 'Clock';
    if (status === 'offline') return 'X';
    
    // Handle availability status (fallback)
    if (typeof status === 'string') {
      switch(status.toLowerCase()) {
        case 'available':
          return 'Check';
        case 'partially_available':
        case 'busy':
          return 'Clock';
        case 'unavailable':
          return 'X';
        default:
          return 'X';
      }
    }
    
    return 'X';
  };

  const getCompletionColor = () => {
    if (completionPercentage >= 80) return 'text-green-300';
    if (completionPercentage >= 50) return 'text-yellow-300';
    return 'text-red-300';
  };
  
  const _getCompletionStatus = () => {
    if (completionPercentage >= 95) return 'Complete';
    if (completionPercentage >= 75) return 'Strong';
    if (completionPercentage >= 50) return 'Good';
    if (completionPercentage >= 25) return 'Basic';
    return 'Incomplete';
  };
  
  const countVerifiedSkills = () => {
    if (!profile?.skills || !Array.isArray(profile.skills)) return 0;
    
    // Get verified skill names from profile
    const verifiedCount = new Set();
    
    // Count from profile.skills
    profile.skills.forEach(skill => {
      const skillName = typeof skill === 'string' ? skill : skill?.name;
      if (skillName) {
        // Check if verified in profile JSON
        if (skill.verified || (skill.ai_assessed && skill.ai_assessment_score >= 70)) {
          verifiedCount.add(skillName);
        }
      }
    });
    
    // Check for AI assessments from database (passed from parent on public profiles)
    aiAssessments.forEach((assessment, skillName) => {
      if (assessment.final_score && Number(assessment.final_score) >= 70) {
        verifiedCount.add(skillName);
      }
    });
    
    return verifiedCount.size;
  };
  
  const getMissingProfileItems = () => {
    if (!profile) return [];
    
    const missingItems = [];
    
    // Check required fields from PROFILE_CONSTANTS
    if (!profile.full_name || profile.full_name.trim() === '') 
      missingItems.push('Add your full name');
    
    if (!profile.title || profile.title.trim() === '') 
      missingItems.push('Add a professional title');
    
    if (!profile.bio || profile.bio.length < 30) 
      missingItems.push('Add a professional bio (at least 30 characters)');
    
    if (!profile.location || profile.location.trim() === '') 
      missingItems.push('Add your location');
    
    if (!profile.hourly_rate || profile.hourly_rate <= 0) 
      missingItems.push('Set your hourly rate');
    
    if (!profile.experience_years || profile.experience_years < 0) 
      missingItems.push('Specify your years of experience');
    
    if (!profile.availability_status || profile.availability_status.trim() === '') 
      missingItems.push('Update your availability status');
    
    if (!profile.skills || profile.skills.length < 3) 
      missingItems.push(`Add at least ${profile.skills ? 3 - profile.skills.length : 3} more skills`);
    
    // Check important optional fields
    if (!profile.profile_picture || profile.profile_picture.trim() === '') 
      missingItems.push('Upload a professional profile picture');
    
    if (!profile.portfolio_items || profile.portfolio_items.length === 0) 
      missingItems.push('Add portfolio items to showcase your work');
      
    if (!profile.timezone || profile.timezone.trim() === '') 
      missingItems.push('Set your timezone');
    
    if (!profile.phone || profile.phone.trim() === '') 
      missingItems.push('Add your phone number');
    
    if (!profile.experience_level || profile.experience_level.trim() === '') 
      missingItems.push('Specify your experience level');
    
    return missingItems.slice(0, 4); // Return top 4 missing items
  };

  return (
    <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
      <div className="content-container py-12">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          {/* Avatar */}
          <div className="relative">
            <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-white shadow-lg">
              {isLoading ? (
                <div className="h-24 w-24 rounded-full bg-gray-200 animate-pulse" />
              ) : (
                <Image
                  src={profile?.profile_picture || '/assets/images/default-avatar.png'}
                  alt={`${profile?.full_name || 'Virtual Assistant'}'s profile`}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
          </div>
          
          {/* Profile Info */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row justify-between items-center md:items-start mb-4">
              <div>
                {isLoading ? (
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <>
                    <h1 className="text-3xl font-bold mb-1 text-center md:text-left">{profile?.full_name || 'Virtual Assistant'}</h1>
                    {profile?.identity_verification_status === 'verified' && (
                      <div className="flex justify-center md:justify-start mb-2">
                        <span
                          className="inline-flex items-center gap-1.5 px-3 py-1 bg-white text-emerald-600 rounded-full text-xs font-bold shadow-md"
                          title="VA has been verified by VA Sprouts"
                        >
                          <Icon name="ShieldCheck" size={13} className="text-emerald-500" />
                          Identity Verified
                        </span>
                      </div>
                    )}
                    <p className="text-primary-100 mb-2 text-center md:text-left">{profile?.title || 'Professional Virtual Assistant'}</p>
                    <div className="flex flex-wrap gap-4 text-sm items-center md:items-start">
                      {profile?.location && (
                        <div className="flex items-center">
                          <Icon name="MapPin" size={16} className="mr-1" />
                          <span>{profile.location}</span>
                        </div>
                      )}
                      {profile?.timezone && (
                        <div className="flex items-center">
                          <Icon name="Clock" size={16} className="mr-1" />
                          <span>{profile.timezone}</span>
                        </div>
                      )}
                      {profile?.joinDate && (
                        <div className="flex items-center">
                          <Icon name="Calendar" size={16} className="mr-1" />
                          <span>Joined {profile.joinDate}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Rate Range and Preferred Project Types */}
                    <div className="flex flex-col md:flex-row gap-2 items-center mt-4">
                      {/* Rate Range */}
                      {(profile?.minimum_rate || profile?.maximum_rate || profile?.hourly_rate) && (
                        <div className="flex items-center gap-2">
                          <div className="bg-white/10 rounded-lg px-3 py-1.5 backdrop-blur-sm">
                            <div className="flex items-center">
                              <Icon name="DollarSign" size={16} className="mr-1.5" />
                              <span className="text-sm font-medium">
                                {profile.minimum_rate && profile.maximum_rate
                                  ? `${getCurrencySymbol(profile.preferredCurrency || profile.preferred_currency)}${profile.minimum_rate} - ${getCurrencySymbol(profile.preferredCurrency || profile.preferred_currency)}${profile.maximum_rate}/hr`
                                  : profile.hourly_rate
                                    ? `${getCurrencySymbol(profile.preferredCurrency || profile.preferred_currency)}${profile.hourly_rate}/hr`
                                    : 'Rate available upon request'
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Preferred Project Types */}
                      {(profile?.preferred_project_types || profile?.preferredProjectTypes) && (profile.preferred_project_types?.length > 0 || profile.preferredProjectTypes?.length > 0) && (
                        <div className="flex items-start gap-2">
                          <div className="bg-white/10 rounded-lg px-3 py-1.5 backdrop-blur-sm">
                            <div className="flex items-center flex-wrap gap-1">
                              <Icon name="Briefcase" size={16} className="mr-1" />
                              <span className="text-sm font-medium mr-2">Preferred:</span>
                              <div className="flex flex-wrap gap-1">
                                {(profile.preferred_project_types || profile.preferredProjectTypes)?.map((type, index) => (
                                  <span key={index} className="bg-white/20 px-2 py-0.5 rounded text-xs">
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex gap-2 mt-4 md:mt-0 md:flex-col lg:flex-row">
                <Button 
                  variant="outline-light" 
                  icon={<Icon name="Share2" size={16} />}
                  iconPosition="left"
                  onClick={handleShareProfile}
                  disabled={isLoading}
                >
                  Share
                </Button>
                {!isPublicView && (
                  <Button 
                    variant="light" 
                    icon={<Icon name="Edit" size={16} />}
                    iconPosition="left"
                    onClick={handleEditProfile}
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
            </div>
            
            {/* Profile Completion - Hidden in public view and when 100% complete */}
            {!isPublicView && completionPercentage < 100 && (
              <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm mb-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium">Profile Completion</span>
                    <div className="relative ml-1 group">
                      <Icon name="HelpCircle" size={16} className="text-white/70 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 w-64 bg-gray-900 text-white text-xs rounded p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                        Complete your profile to increase visibility with potential employers. 
                        A 100% complete profile is 70% more likely to receive job offers.
                      </div>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${getCompletionColor()}`}>{completionPercentage}%</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      completionPercentage >= 80 
                        ? 'bg-green-400' 
                        : completionPercentage >= 50 
                          ? 'bg-yellow-400' 
                          : 'bg-red-400 animate-pulse'
                    }`}
                    style={{ width: `${completionPercentage}%` }}
                  ></div>
                </div>
                
                {/* Profile completion status markers */}
                <div className="flex justify-between mt-1 px-1">
                  <div className={`text-xs ${completionPercentage >= 25 ? 'text-white' : 'text-white/50'}`}>Basic</div>
                  <div className={`text-xs ${completionPercentage >= 50 ? 'text-white' : 'text-white/50'}`}>Good</div>
                  <div className={`text-xs ${completionPercentage >= 75 ? 'text-white' : 'text-white/50'}`}>Strong</div>
                  <div className={`text-xs ${completionPercentage >= 95 ? 'text-white' : 'text-white/50'}`}>Complete</div>
                </div>
                
                {/* Completion tips */}
                {completionPercentage < 100 && (
                  <div className="mt-2 text-xs text-white/80 bg-white/5 p-2 rounded">
                    <div className="flex items-center">
                      <Icon name="AlertCircle" size={14} className="mr-1 text-yellow-300" />
                      <span className="font-medium">Next steps to improve your profile:</span>
                    </div>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                      {getMissingProfileItems().map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                      {getMissingProfileItems().length === 0 && completionPercentage < 100 && (
                        <li>Add more details to your profile for higher visibility</li>
                      )}
                    </ul>
                    {!isPublicView && (
                      <Button 
                        variant="outline-light" 
                        size="sm"
                        className="mt-2 w-full"
                        icon={<Icon name="Edit" size={14} />}
                        iconPosition="left"
                        onClick={handleEditProfile}
                      >
                        Complete Your Profile
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Metrics */}
            <div className="relative">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium text-white/90">Performance Metrics</h3>
                <button 
                  className="text-xs text-white/70 hover:text-white flex items-center" 
                  onClick={() => setShowCompletionDetails(!showCompletionDetails)}
                >
                  <Icon name="Info" size={14} className="mr-1" />
                  <span>What's this?</span>
                </button>
              </div>
              
              {showCompletionDetails && (
                <div className="absolute right-0 top-8 w-64 bg-gray-900 p-3 rounded-lg shadow-lg z-20 text-xs text-white/90">
                  <h4 className="font-medium mb-1">About Metrics</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Icon name="Star" size={14} className="text-yellow-300 mr-1 mt-0.5" />
                      <span><strong>Rating:</strong> Average client satisfaction score</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="CheckCircle" size={14} className="text-green-300 mr-1 mt-0.5" />
                      <span><strong>Jobs:</strong> Number of successfully completed projects</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Award" size={14} className="text-blue-300 mr-1 mt-0.5" />
                      <span><strong>Verified Skills:</strong> Skills passed assessment</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Briefcase" size={14} className="text-orange-300 mr-1 mt-0.5" />
                      <span><strong>Experience:</strong> Work experience entries added to profile</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Users" size={14} className="text-purple-300 mr-1 mt-0.5" />
                      <span><strong>Clients:</strong> Unique clients who have hired this VA</span>
                    </li>
                  </ul>
                  <button 
                    className="mt-2 text-white/70 hover:text-white text-xs flex items-center"
                    onClick={() => setShowCompletionDetails(false)}
                  >
                    <Icon name="X" size={12} className="mr-1" />
                    Close
                  </button>
                </div>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {isLoading ? (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                      <div className="h-6 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm relative group">
                    <div className="flex items-center mb-1">
                      <Icon name="Star" size={16} className="text-yellow-300 mr-2" />
                      <span className="text-xl font-bold">{profile?.rating || '0.0'}</span>
                      <span className="text-sm ml-1">/ 5</span>
                    </div>
                    <p className="text-xs text-primary-100">
                      {profile?.reviewCount || 0} {profile?.reviewCount === 1 ? 'review' : 'reviews'}
                    </p>
                    {parseFloat(profile?.rating || 0) >= 4.5 && (
                      <div className="absolute -top-1 -right-1">
                        <span className="bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded-full flex items-center">
                          <Icon name="Award" size={10} className="mr-0.5" />
                          Top Rated
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg z-10 pointer-events-none">
                      <p className="text-xs text-white text-center px-2">Based on client reviews and feedback</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm relative group">
                    <div className="flex items-center mb-1">
                      <Icon name="Award" size={16} className="text-blue-300 mr-2" />
                      <span className="text-xl font-bold">{countVerifiedSkills()}</span>
                    </div>
                    <p className="text-xs text-primary-100">Verified skills</p>
                    {countVerifiedSkills() > 0 && (
                      <div className="absolute -top-1 -right-1">
                        <span className={`${
                          countVerifiedSkills() >= 10 ? 'bg-blue-500' : countVerifiedSkills() >= 5 ? 'bg-blue-600' : 'bg-blue-700'
                        } text-white text-xs px-1.5 py-0.5 rounded-full`}>
                          {countVerifiedSkills() >= 10 ? 'Expert' : countVerifiedSkills() >= 5 ? 'Strong' : 'Growing'}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg z-10 pointer-events-none">
                      <p className="text-xs text-white text-center px-2">Skills verified through assessments</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm relative group">
                    <div className="flex items-center mb-1">
                      <Icon name="Briefcase" size={16} className="text-green-300 mr-2" />
                      <span className="text-xl font-bold">{profile?.portfolio_items?.length || 0}</span>
                    </div>
                    <p className="text-xs text-primary-100">Portfolio items</p>
                    {(profile?.portfolio_items?.length || 0) > 0 && (
                      <div className="absolute -top-1 -right-1">
                        <span className={`${
                          (profile?.portfolio_items?.length || 0) >= 5 ? 'bg-green-500' : 'bg-green-700'
                        } text-white text-xs px-1.5 py-0.5 rounded-full`}>
                          {(profile?.portfolio_items?.length || 0) >= 10 ? 'Showcase' : (profile?.portfolio_items?.length || 0) >= 5 ? 'Strong' : 'Growing'}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg z-10 pointer-events-none">
                      <p className="text-xs text-white text-center px-2">Portfolio projects showcasing skills and work samples</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm relative group">
                    <div className="flex items-center mb-1">
                      <Icon name="Briefcase" size={16} className="text-orange-300 mr-2" />
                      <span className="text-xl font-bold">{profile?.workExperienceCount || 0}</span>
                    </div>
                    <p className="text-xs text-primary-100">Work experiences</p>
                    {profile?.workExperienceCount > 0 && (
                      <div className="absolute -top-1 -right-1">
                        <span className={`${
                          profile.workExperienceCount >= 5 ? 'bg-orange-500' : 'bg-orange-700'
                        } text-white text-xs px-1.5 py-0.5 rounded-full`}>
                          {profile.workExperienceCount >= 10 ? 'Extensive' : profile.workExperienceCount >= 5 ? 'Strong' : 'Growing'}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg z-10 pointer-events-none">
                      <p className="text-xs text-white text-center px-2">Professional experience entries added to profile</p>
                    </div>
                  </div>
                  
                  <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm relative group">
                    <div className="flex items-center mb-1">
                      <Icon name="Users" size={16} className="text-purple-300 mr-2" />
                      <span className="text-xl font-bold">{profile?.clientCount || 0}</span>
                    </div>
                    <p className="text-xs text-primary-100">Clients worked with</p>
                    {profile?.clientCount >= 5 && (
                      <div className="absolute -top-1 -right-1">
                        <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                          <Icon name="ThumbsUp" size={10} className="mr-0.5" />
                          Trusted
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg z-10 pointer-events-none">
                      <p className="text-xs text-white text-center px-2">Unique clients who have hired this VA</p>
                    </div>
                  </div>
                </>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              {!isLoading && !isPublicView && (
                <Button 
                  className="bg-white text-primary-600 hover:bg-white/90"
                  icon={<Icon name="Briefcase" size={16} />}
                  iconPosition="left"
                  onClick={() => navigate('/va-portfolio-management')}
                >
                  Manage Portfolio
                </Button>
              )}
              {!isLoading && profile?.website && (
                <a href={profile.website} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-white text-primary-600 hover:bg-white/90" icon={<Icon name="Globe" size={16} />} iconPosition="left">
                    Website
                  </Button>
                </a>
              )}
              {!isLoading && profile?.linkedin && (
                <a href={profile.linkedin} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-white text-primary-600 hover:bg-white/90" icon={<Icon name="Linkedin" size={16} />} iconPosition="left">
                    LinkedIn
                  </Button>
                </a>
              )}
              {!isLoading && profile?.portfolio_url && (
                <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-white text-primary-600 hover:bg-white/90" icon={<Icon name="ExternalLink" size={16} />} iconPosition="left">
                    Portfolio
                  </Button>
                </a>
              )}
              {!isLoading && profile?.github_url && (
                <a href={profile.github_url} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-white text-primary-600 hover:bg-white/90" icon={<Icon name="GitHub" size={16} />} iconPosition="left">
                    GitHub
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHero;
